import { useContext, useEffect, useLayoutEffect, useRef } from "react";
import { withRouter } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useGetChatEnabledQuery, useGetChatEligibilityQuery } from "@api/chatManagement";
import {
	setChatEligibility,
	setChatPanelOpen,
} from "@actions/chat";
import { MainContext } from "@context";
import { useIsLoggedIn, isChatGloballyEnabled } from "../utils";
import "../styles/shell.css";

const ELIGIBILITY_POLL_MS = 60_000;

const TRANSITION_VAR = "--chat-drawer-transition";
const APP_WRAPPER_ID = "appWrapper";
const APP_ROUTE_ID = "app-route";
const WRAPPER_MAIN_ID = "WrapperMain";
const CHAT_PORTAL_ID = "chat-portal";
const OPEN_CLASS = "chat-open";
const MOBILE_CLASS = "chat-open-mobile";
const SHELL_READY_CLASS = "chat-shell-ready";
const SUPPRESSED_CLASS = "chat-suppressed-fixed";

const getDrawerWidthPx = () => {
	const raw = getComputedStyle(document.documentElement)
		.getPropertyValue("--chat-drawer-width")
		.trim();

	if (!raw || raw === "100%") {
		return window.innerWidth;
	}

	if (raw.endsWith("px")) {
		return parseFloat(raw);
	}

	if (raw.startsWith("min(") && raw.endsWith(")")) {
		const parts = raw
			.slice(4, -1)
			.split(",")
			.map((part) => part.trim());

		const resolved = parts.map((part) => {
			if (part.endsWith("px")) {
				return parseFloat(part);
			}

			if (part.endsWith("vw")) {
				return (window.innerWidth * parseFloat(part)) / 100;
			}

			return Number.POSITIVE_INFINITY;
		});

		return Math.min(...resolved);
	}

	if (raw.includes("vw")) {
		const value = parseFloat(raw);
		return (window.innerWidth * value) / 100;
	}

	const chatRoot = document.querySelector("#chat-portal .chat-root.is-open");

	if (chatRoot) {
		return Math.round(chatRoot.getBoundingClientRect().width);
	}

	return 420;
};

const scheduleWindowResize = () => {
	requestAnimationFrame(() => {
		window.dispatchEvent(new Event("resize"));
		window.setTimeout(() => window.dispatchEvent(new Event("resize")), getTransitionMs() + 50);
	});
};

const getTransitionMs = () => {
	const raw = getComputedStyle(document.documentElement).getPropertyValue(TRANSITION_VAR).trim();
	const secondsMatch = raw.match(/([\d.]+)s/);

	if (secondsMatch) {
		return Math.round(parseFloat(secondsMatch[1]) * 1000);
	}

	const msMatch = raw.match(/([\d.]+)ms/);

	if (msMatch) {
		return Math.round(parseFloat(msMatch[1]));
	}

	return 300;
};

const tierHeaderHeight = {
	mobile: 60,
	tablet: 60,
	desktop: 70,
	wide: 70,
	ultra: 90,
};

const getWidthTier = (width) => {
	if (width <= 767) {
		return "mobile";
	}

	if (width <= 1023) {
		return "tablet";
	}

	if (width <= 1279) {
		return "desktop";
	}

	if (width <= 1579) {
		return "wide";
	}

	return "ultra";
};

const readAppWrapper = () => document.getElementById(APP_WRAPPER_ID);

const isShellContainerActive = () =>
	document.documentElement.classList.contains(SHELL_READY_CLASS);

const syncAppShellCssVars = (appWrapper = readAppWrapper()) => {
	if (!isShellContainerActive() || !appWrapper) {
		return null;
	}

	const width = Math.round(appWrapper.getBoundingClientRect().width);

	if (!width) {
		return null;
	}

	const tier = getWidthTier(width);
	const headerHeight = tierHeaderHeight[tier] ?? 60;

	appWrapper.style.setProperty("--shell-header-height", `${headerHeight}px`);
	document.documentElement.style.setProperty("--shell-header-height", `${headerHeight}px`);

	return { width, tier };
};

const clearAppShellCssVars = (appWrapper = readAppWrapper()) => {
	appWrapper?.style.removeProperty("--shell-header-height");
	document.documentElement.style.removeProperty("--shell-header-height");
};

const FIXED_UI_SELECTORS = [
	".scroll-to-top",
	"#launcher",
	"#webWidget",
	"iframe#webWidget",
	".support-chat-fixed-widget",
].join(",");

const shouldNeverSuppressFixedUi = (el) => {
	if (!el || el.nodeType !== 1) {
		return true;
	}

	if (el.closest(`#${CHAT_PORTAL_ID}`)) {
		return true;
	}

	if (el.closest(".bm-menu-wrap, .bm-burger-button, .bm-overlay, .bm-menu")) {
		return true;
	}

	if (el.closest("#appWrapper .header, .header")) {
		return true;
	}

	if (el.closest(".casino-tabs") || el.classList.contains("ant-tabs-nav")) {
		return true;
	}

	return false;
};

const suppressOverlappingFixedUi = (isMobile = false) => {
	const hidden = [];
	const drawerWidth = getDrawerWidthPx();
	const overlapThreshold = isMobile ? window.innerWidth : window.innerWidth - drawerWidth + 4;

	document.querySelectorAll(FIXED_UI_SELECTORS).forEach((el) => {
		if (shouldNeverSuppressFixedUi(el)) {
			return;
		}

		el.classList.add(SUPPRESSED_CLASS);
		hidden.push(el);
	});

	document.querySelectorAll("body *").forEach((el) => {
		if (shouldNeverSuppressFixedUi(el) || hidden.includes(el)) {
			return;
		}

		const style = getComputedStyle(el);

		if (style.position !== "fixed") {
			return;
		}

		if (isMobile) {
			el.classList.add(SUPPRESSED_CLASS);
			hidden.push(el);
			return;
		}

		const rect = el.getBoundingClientRect();

		if (rect.width > 0 && rect.right > overlapThreshold) {
			el.classList.add(SUPPRESSED_CLASS);
			hidden.push(el);
		}
	});

	return hidden;
};

let pendingShellLayoutFrame = null;

const syncShellLayout = (appWrapper) => {
	if (!appWrapper) {
		return;
	}

	syncAppShellCssVars(appWrapper);
};

const scheduleSyncShellLayout = (appWrapper) => {
	if (pendingShellLayoutFrame) {
		cancelAnimationFrame(pendingShellLayoutFrame);
	}

	pendingShellLayoutFrame = requestAnimationFrame(() => {
		pendingShellLayoutFrame = null;
		syncShellLayout(appWrapper);
	});
};

const clearShellLayout = (appWrapper) => {
	if (pendingShellLayoutFrame) {
		cancelAnimationFrame(pendingShellLayoutFrame);
		pendingShellLayoutFrame = null;
	}

	clearAppShellCssVars(appWrapper);
};

const waitForShellTransition = (appWrapper, callback) => {
	if (!appWrapper) {
		callback();
		return () => {};
	}

	let settled = false;

	const finish = () => {
		if (settled) {
			return;
		}

		settled = true;
		appWrapper.removeEventListener("transitionend", onTransitionEnd);
		clearTimeout(fallbackTimer);
		callback();
	};

	const cancel = () => {
		if (settled) {
			return;
		}

		settled = true;
		appWrapper.removeEventListener("transitionend", onTransitionEnd);
		clearTimeout(fallbackTimer);
	};

	const onTransitionEnd = (event) => {
		if (event.target === appWrapper && event.propertyName === "transform") {
			finish();
		}
	};

	appWrapper.addEventListener("transitionend", onTransitionEnd);
	const fallbackTimer = setTimeout(finish, getTransitionMs() + 80);

	return cancel;
};

let globalDrawerPhase = "closed";
let shellResizeObserver = null;
let shellWindowResizeHandler = null;

const disconnectShellResizeObservers = () => {
	shellResizeObserver?.disconnect?.();
	shellResizeObserver = null;

	if (shellWindowResizeHandler) {
		window.removeEventListener("resize", shellWindowResizeHandler);
		shellWindowResizeHandler = null;
	}
};

const getRouteKey = (location) => `${location.pathname}${location.search}${location.hash}`;

const ChatLayoutInner = ({ location }) => {
	const dispatch = useDispatch();
	const { state } = useContext(MainContext);
	const isLoggedIn = useIsLoggedIn();
	const isMobileLayout = Boolean(state?.isMobile || state?.isTablet);
	const isPanelOpen = useSelector((reduxState) => Boolean(reduxState.chat?.isPanelOpen));
	const isChatEnabled = useSelector((state) => state.chat?.isChatEnabled !== false);
	const eligibilityLoaded = useSelector((state) => Boolean(state.chat?.eligibilityLoaded));
	const routeKey = getRouteKey(location);
	const wrapperMainOverflowRef = useRef({ overflow: "", overflowX: "" });
	const bodyOverflowRef = useRef("");
	const hiddenElementsRef = useRef([]);
	const cancelWaitRef = useRef(null);
	const phaseRef = useRef(globalDrawerPhase);

	const { data: chatEnabledData, isSuccess: chatEnabledSuccess, isError: chatEnabledError } =
		useGetChatEnabledQuery(undefined, {
			skip: isLoggedIn,
			pollingInterval: isLoggedIn ? 0 : ELIGIBILITY_POLL_MS,
		});

	const { data: eligibility, isSuccess: eligibilitySuccess, isError: eligibilityError } =
		useGetChatEligibilityQuery(undefined, {
			skip: !isLoggedIn,
			pollingInterval: isLoggedIn ? ELIGIBILITY_POLL_MS : 0,
		});

	useEffect(() => {
		dispatch(
			setChatEligibility({
				chatEnabled: true,
				loaded: false,
			})
		);
	}, [isLoggedIn, dispatch]);

	useEffect(() => {
		if (isLoggedIn) {
			if (eligibilitySuccess) {
				dispatch(
					setChatEligibility({
						chatEnabled: isChatGloballyEnabled(eligibility),
						loaded: true,
					})
				);
				return;
			}

			if (eligibilityError) {
				dispatch(
					setChatEligibility({
						chatEnabled: true,
						loaded: true,
					})
				);
			}

			return;
		}

		if (chatEnabledSuccess) {
			dispatch(
				setChatEligibility({
					chatEnabled: isChatGloballyEnabled(chatEnabledData),
					loaded: true,
				})
			);
			return;
		}

		if (chatEnabledError) {
			dispatch(
				setChatEligibility({
					chatEnabled: false,
					loaded: true,
				})
			);
		}
	}, [
		isLoggedIn,
		eligibility,
		eligibilitySuccess,
		eligibilityError,
		chatEnabledData,
		chatEnabledSuccess,
		chatEnabledError,
		dispatch,
	]);

	useEffect(() => {
		const chatDisabled = isLoggedIn
			? eligibilitySuccess && !isChatGloballyEnabled(eligibility)
			: chatEnabledSuccess && !isChatGloballyEnabled(chatEnabledData);

		if (chatDisabled && isPanelOpen) {
			dispatch(setChatPanelOpen(false));
		}
	}, [
		isLoggedIn,
		eligibility,
		eligibilitySuccess,
		chatEnabledData,
		chatEnabledSuccess,
		isPanelOpen,
		dispatch,
	]);

	const setPhase = (phase) => {
		globalDrawerPhase = phase;
		phaseRef.current = phase;
	};

	useLayoutEffect(() => {
		if (eligibilityLoaded && !isChatEnabled) {
			const appWrapper = document.getElementById(APP_WRAPPER_ID);
			const appRoute = document.getElementById(APP_ROUTE_ID);
			const wrapperMain = document.getElementById(WRAPPER_MAIN_ID);
			const chatPortal = document.getElementById(CHAT_PORTAL_ID);
			const { body, documentElement } = document;

			cancelWaitRef.current?.();
			cancelWaitRef.current = null;
			disconnectShellResizeObservers();
			documentElement.classList.remove(OPEN_CLASS, MOBILE_CLASS, SHELL_READY_CLASS);
			appRoute?.classList.remove(OPEN_CLASS);
			appWrapper?.classList.remove(OPEN_CLASS);
			wrapperMain?.classList.remove(OPEN_CLASS);
			chatPortal?.classList.remove(MOBILE_CLASS);
			body.style.overflow = bodyOverflowRef.current;
			hiddenElementsRef.current.forEach((el) => {
				el.classList.remove(SUPPRESSED_CLASS);
			});
			hiddenElementsRef.current = [];
			document.querySelectorAll(`.${SUPPRESSED_CLASS}`).forEach((el) => {
				el.classList.remove(SUPPRESSED_CLASS);
			});
			if (wrapperMain) {
				wrapperMain.style.overflow = wrapperMainOverflowRef.current.overflow;
				wrapperMain.style.overflowX = wrapperMainOverflowRef.current.overflowX;
			}
			clearShellLayout(appWrapper);
			setPhase("closed");

			return undefined;
		}

		const appWrapper = document.getElementById(APP_WRAPPER_ID);
		const appRoute = document.getElementById(APP_ROUTE_ID);
		const wrapperMain = document.getElementById(WRAPPER_MAIN_ID);
		const chatPortal = document.getElementById(CHAT_PORTAL_ID);
		const { body, documentElement } = document;

		const cancelWait = () => {
			cancelWaitRef.current?.();
			cancelWaitRef.current = null;
		};

		const restoreFixedUi = () => {
			hiddenElementsRef.current.forEach((el) => {
				el.classList.remove(SUPPRESSED_CLASS);
			});
			hiddenElementsRef.current = [];
		};

		const restoreWrapperMainOverflow = () => {
			if (!wrapperMain) {
				return;
			}

			wrapperMain.classList.remove(OPEN_CLASS);
			wrapperMain.style.overflow = wrapperMainOverflowRef.current.overflow;
			wrapperMain.style.overflowX = wrapperMainOverflowRef.current.overflowX;
		};

		const setDomOpen = (open) => {
			const isMobileOpen = open && isMobileLayout;
			const isDesktopOpen = open && !isMobileLayout;

			documentElement.classList.toggle(OPEN_CLASS, open);
			documentElement.classList.toggle(MOBILE_CLASS, isMobileOpen);
			appRoute?.classList.toggle(OPEN_CLASS, isDesktopOpen);
			appWrapper?.classList.toggle(OPEN_CLASS, isDesktopOpen);
			wrapperMain?.classList.toggle(OPEN_CLASS, isDesktopOpen);
			chatPortal?.classList.toggle(MOBILE_CLASS, isMobileOpen);

			if (isMobileOpen) {
				bodyOverflowRef.current = body.style.overflow;
				body.style.overflow = "hidden";
			} else if (!open) {
				body.style.overflow = bodyOverflowRef.current;
			}
		};

		const applyDesktopOverflowGuards = () => {
			if (!wrapperMain) {
				return;
			}

			wrapperMainOverflowRef.current = {
				overflow: wrapperMain.style.overflow,
				overflowX: wrapperMain.style.overflowX,
			};
			wrapperMain.classList.add(OPEN_CLASS);
			wrapperMain.style.overflowX = "hidden";
		};

		const scrubSuppressedFixedUi = () => {
			document.querySelectorAll(`.${SUPPRESSED_CLASS}`).forEach((el) => {
				el.classList.remove(SUPPRESSED_CLASS);
			});
		};

		const teardownShellDom = () => {
			cancelWait();
			disconnectShellResizeObservers();
			setDomOpen(false);
			documentElement.classList.remove(SHELL_READY_CLASS);
			appRoute?.classList.remove(OPEN_CLASS);
			appWrapper?.classList.remove(OPEN_CLASS);
			wrapperMain?.classList.remove(OPEN_CLASS);
			chatPortal?.classList.remove(MOBILE_CLASS);
			body.style.overflow = bodyOverflowRef.current;
			restoreFixedUi();
			scrubSuppressedFixedUi();
			restoreWrapperMainOverflow();
			clearShellLayout(appWrapper);
			setPhase("closed");
			scheduleWindowResize();
		};

		const stopShellLayout = () => {
			disconnectShellResizeObservers();
			documentElement.classList.remove(SHELL_READY_CLASS);
			clearShellLayout(appWrapper);
		};

		const startShellLayout = (wrapper = appWrapper) => {
			if (!wrapper) {
				return;
			}

			disconnectShellResizeObservers();
			documentElement.classList.add(SHELL_READY_CLASS);
			void wrapper.offsetHeight;

			const update = () => {
				if (!wrapper.isConnected) {
					return;
				}

				scheduleSyncShellLayout(wrapper);
			};

			update();

			shellWindowResizeHandler = update;
			window.addEventListener("resize", shellWindowResizeHandler);

			if (typeof ResizeObserver !== "undefined") {
				shellResizeObserver = new ResizeObserver(update);
				shellResizeObserver.observe(wrapper);
			}
		};

		const settleOpen = () => {
			setPhase("open");
			startShellLayout();
			applyDesktopOverflowGuards();
			restoreFixedUi();
			hiddenElementsRef.current = suppressOverlappingFixedUi(false);
			scheduleWindowResize();
		};

		const resyncSettledOpen = () => {
			const freshWrapper = document.getElementById(APP_WRAPPER_ID);
			const freshWrapperMain = document.getElementById(WRAPPER_MAIN_ID);
			const freshAppRoute = document.getElementById(APP_ROUTE_ID);
			const freshChatPortal = document.getElementById(CHAT_PORTAL_ID);

			if (!freshWrapper) {
				requestAnimationFrame(resyncSettledOpen);
				return;
			}

			cancelWait();
			restoreFixedUi();
			setPhase("open");

			documentElement.classList.add(OPEN_CLASS);
			documentElement.classList.remove(MOBILE_CLASS);
			documentElement.classList.add(SHELL_READY_CLASS);
			freshAppRoute?.classList.add(OPEN_CLASS);
			freshWrapper.classList.add(OPEN_CLASS);
			freshChatPortal?.classList.remove(MOBILE_CLASS);

			startShellLayout(freshWrapper);

			if (freshWrapperMain) {
				wrapperMainOverflowRef.current = {
					overflow: freshWrapperMain.style.overflow,
					overflowX: freshWrapperMain.style.overflowX,
				};
				freshWrapperMain.classList.add(OPEN_CLASS);
				freshWrapperMain.style.overflowX = "hidden";
			}

			hiddenElementsRef.current = suppressOverlappingFixedUi(false);
			scheduleWindowResize();
		};

		const openDesktopDrawer = () => {
			if (phaseRef.current === "open" || phaseRef.current === "opening") {
				return;
			}

			cancelWait();
			stopShellLayout();
			restoreFixedUi();
			setPhase("opening");

			const startOpenTransition = () => {
				setDomOpen(true);

				cancelWaitRef.current = waitForShellTransition(appWrapper, () => {
					cancelWaitRef.current = null;

					if (phaseRef.current !== "opening") {
						return;
					}

					settleOpen();
				});
			};

			if (documentElement.classList.contains(OPEN_CLASS)) {
				startOpenTransition();
				return;
			}

			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					if (phaseRef.current !== "opening") {
						return;
					}

					startOpenTransition();
				});
			});
		};

		const closeDesktopDrawer = () => {
			if (phaseRef.current === "closed") {
				teardownShellDom();
				return;
			}

			if (phaseRef.current === "closing") {
				return;
			}

			setPhase("closing");
			teardownShellDom();
		};

		if (isMobileLayout) {
			if (isPanelOpen) {
				stopShellLayout();
				setDomOpen(true);
				setPhase("open");
				restoreFixedUi();
				hiddenElementsRef.current = suppressOverlappingFixedUi(true);
				scheduleWindowResize();
			} else {
				teardownShellDom();
			}

			return () => {
				cancelWait();
				if (!isPanelOpen) {
					teardownShellDom();
				} else {
					disconnectShellResizeObservers();
				}
			};
		}

		if (isPanelOpen) {
			const shellShouldBeOpen =
				globalDrawerPhase === "open" ||
				globalDrawerPhase === "opening" ||
				(documentElement.classList.contains(OPEN_CLASS) &&
					documentElement.classList.contains(SHELL_READY_CLASS));
			const wrapperNeedsSync = !appWrapper?.classList.contains(OPEN_CLASS);

			if (shellShouldBeOpen && wrapperNeedsSync) {
				resyncSettledOpen();
			} else if (shellShouldBeOpen) {
				setDomOpen(true);
				startShellLayout(appWrapper);
				applyDesktopOverflowGuards();
				restoreFixedUi();
				hiddenElementsRef.current = suppressOverlappingFixedUi(false);
				scheduleWindowResize();
			} else if (globalDrawerPhase === "closing") {
				resyncSettledOpen();
			} else {
				openDesktopDrawer();
			}
		} else {
			closeDesktopDrawer();
		}

		return () => {
			cancelWait();
			if (!isPanelOpen) {
				teardownShellDom();
			}
		};
	}, [isPanelOpen, isMobileLayout, routeKey, eligibilityLoaded, isChatEnabled]);

	return null;
};

export const ChatLayout = withRouter(ChatLayoutInner);
